/**
 * Unified inbox API — Guest Comms stage 1, Inc 3 (read) + Inc 4 (reply).
 *
 * JWT endpoints → shared `secureApi` instance (per CLAUDE.md dual-axios rule).
 * Core-staff only server-side; an owner gets 403 from the router, never a filtered view.
 *
 * FLAG BEHAVIOUR, deliberate: the backend mounts /api/v1/conversations only when
 * CONVERSATION_STORE_ENABLED === 'true'. With the flag off the route does not exist and Express
 * serves its default 404 — which is HTML, not JSON. That is the single kill switch for this feature
 * (there is intentionally no REACT_APP_INBOX_ENABLED: CRA bakes env vars at build time, so a
 * frontend flag could not be flipped without a rebuild and is therefore not a kill switch).
 *
 * `featureUnavailable` below turns that 404 into something the page can render calmly, so a
 * frontend deployed ahead of the flag shows a quiet Arabic notice instead of a crash or a raw
 * English error page.
 */
import { secureApi } from './secureApi';

const BASE = '/api/v1/conversations';

// The server's own ceiling (conversationRoutes.js clamps to 100). Asking for it is not a page size
// so much as "give me everything you will give me" — the page then says out loud when there is more.
export const LIST_LIMIT = 100;

/**
 * True when the endpoint isn't there at all.
 *
 * Distinguished by BODY TYPE, not by a missing `success` flag: an unmounted route falls through to
 * Express's default handler, which returns 404 with an HTML **string** body, whereas every real 404
 * this API raises (e.g. an unknown conversation id) returns a JSON **object** — and that object
 * carries `success: false`, so testing the flag alone would misread a genuine not-found as
 * "feature off". Verified against the live dev server: flag unset ⇒ `Content-Type: text/html`.
 *
 * ⚠️ It CANNOT tell "switched off" from "broken deploy". A frontend shipped ahead of the backend, a
 * changed API prefix, or a rolled-back route all produce the identical HTML 404. So the caller must
 * word the message as "unavailable" rather than "not enabled yet", and must log — otherwise a
 * broken deploy is presented to staff as an intentional state and nobody escalates it.
 */
export const isEndpointMissing = (err) =>
  err?.response?.status === 404 && typeof err?.response?.data !== 'object';

/**
 * One key per COMPOSE ACTION — not per HTTP request. It is what turns "send" into an idempotent
 * operation: every retry of the same typed message replays the same key, and the server's unique
 * index guarantees the guest can never receive it twice. The danger it addresses is not a double
 * click but a lost response, where Channex accepted the message and we never heard back.
 *
 * `crypto.randomUUID` needs a secure context (https or localhost) — both true for this app — but the
 * fallback keeps a stray http:// origin from breaking sending entirely. Collision risk there is
 * irrelevant at this scale, and a collision would at worst refuse a send, never duplicate one.
 */
export const newIdempotencyKey = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `k-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

export const conversationApi = {
  /**
   * The inbox list, newest message first.
   * @param {{page?:number, limit?:number, needsReply?:boolean, unowned?:boolean, propertyId?:string, channel?:string}} params
   */
  list: (params = {}) => {
    // Only send filters that are actually on — the backend treats presence as truth, and an
    // explicit `needsReply=false` would be read as a filter rather than "no filter".
    //
    // `limit` ALWAYS goes: omitting it silently accepted the backend default of 25, which on an
    // inbox means the oldest unanswered threads simply vanish with no signal. The caller passes the
    // server maximum and renders an explicit overflow notice from `pagination.total`.
    const query = { limit: params.limit || LIST_LIMIT };
    if (params.page) query.page = params.page;
    if (params.needsReply) query.needsReply = true;
    if (params.unowned) query.unowned = true;
    if (params.propertyId) query.propertyId = params.propertyId;
    if (params.hidden) query.hidden = true;
    if (params.channel) query.channel = params.channel;
    return secureApi.get(BASE, { params: query }).then((r) => r.data);
  },

  // WhatsApp only (tasks/whatsapp-inbox-4jawaly.md): «ليس ضيفاً» and «ربط بحجز».
  whatsappNotGuest: (id) =>
    secureApi.post(`${BASE}/${id}/whatsapp/not-guest`, {}, { preserveBackendError: true }).then((r) => r.data),
  whatsappRestore: (id) =>
    secureApi.post(`${BASE}/${id}/whatsapp/restore`, {}, { preserveBackendError: true }).then((r) => r.data),
  whatsappBookings: (id, q) =>
    secureApi.get(`${BASE}/${id}/whatsapp/bookings`, { params: { q }, preserveBackendError: true }).then((r) => r.data),
  whatsappLink: (id, bookingId) =>
    secureApi.post(`${BASE}/${id}/whatsapp/booking-link`, { bookingId }, { preserveBackendError: true }).then((r) => r.data),
  whatsappUnlink: (id) =>
    secureApi.delete(`${BASE}/${id}/whatsapp/booking-link`, { preserveBackendError: true }).then((r) => r.data),

  /** One thread's messages, oldest first (reading order). */
  messages: (conversationId, params = {}) =>
    secureApi
      .get(`${BASE}/${conversationId}/messages`, {
        params: { ...(params.page ? { page: params.page } : {}), ...(params.limit ? { limit: params.limit } : {}) }
      })
      .then((r) => r.data),

  /**
   * Send one reply (Inc 4).
   *
   * `idempotencyKey` is generated ONCE per compose action by the caller and replayed on every retry
   * of that action — that is what makes retry safe. Generating one here (per HTTP call) would defeat
   * the whole mechanism, since a retry would look like a brand-new message.
   *
   * `timeout` overrides secureApi's 10s default. It must EXCEED the backend's own 30s Channex
   * timeout, or the browser gives up first and the operator never learns the outcome of a send that
   * is still in flight — they would see a failure for a message that was, in fact, delivered.
   *
   * `preserveBackendError` keeps the route's specific Arabic message (secureApi otherwise rewrites
   * 4xx/5xx bodies to generic strings) — "الرسالة قيد الإرسال بالفعل" and "تعذّر إرسال الرسالة" are
   * different situations and must read differently.
   */
  /**
   * `confirmDuplicate` is the ONLY thing that unlocks re-sending a message whose delivery we could
   * not confirm. It was being dropped here while the page passed it, so «إرسال نسخة أخرى» posted
   * without the flag, the server refused again, and the operator was stuck in the unconfirmed state
   * with no way out. Forwarded explicitly, and only when true — the server tests `=== true`.
   */
  reply: (conversationId, { message, idempotencyKey, confirmDuplicate = false }) =>
    secureApi
      .post(
        `${BASE}/${conversationId}/reply`,
        { message, idempotencyKey, ...(confirmDuplicate ? { confirmDuplicate: true } : {}) },
        { timeout: 35000, preserveBackendError: true }
      )
      .then((r) => r.data),

  /**
   * Send ONE photo (Inc 8, D123).
   *
   * A SIBLING of `reply`, not an option on it. The provider forces text and image down two separate
   * calls, so a compose action carrying both hits `reply` once and this once per photo — each with
   * its OWN `idempotencyKey` (D130). One key per vendor operation is what keeps the guarantee that a
   * retry can never deliver a second copy; a key shared across a text and three photos could not say
   * WHICH of the four it had already sent.
   *
   * Multipart, so the JSON default on the shared instance has to be cleared for this one request.
   * Setting it to `undefined` rather than to 'multipart/form-data' is deliberate: the boundary token
   * is generated per-body, and a hand-written header omits it, which makes the server unable to parse
   * a body it just received.
   *
   * `timeout` is longer than the text reply's: this call carries an upload, an image conversion and a
   * storage write before the vendor round-trip even starts. It must still EXCEED the backend's own
   * Channex timeout, or the browser gives up first and the operator never learns the outcome of a
   * send that was in fact delivered.
   */
  replyPhoto: (conversationId, { file, idempotencyKey, confirmDuplicate = false }) => {
    const form = new FormData();
    form.append('photo', file);
    form.append('idempotencyKey', idempotencyKey);
    if (confirmDuplicate) form.append('confirmDuplicate', 'true');
    return secureApi
      .post(`${BASE}/${conversationId}/reply-photo`, form, {
        timeout: 60000,
        preserveBackendError: true,
        headers: { 'Content-Type': undefined }
      })
      .then((r) => r.data);
  },

  // ---- Inc 5a: team state ----

  /**
   * "I have looked at this thread" — marks it read FOR THIS USER only and records the open.
   *
   * A POST, not a side effect of the GET: a read request that writes is a trap for every proxy and
   * retry between here and Mongo. The caller does NOT await it — D61 says one extra second opening a
   * thread sends staff back to the Airbnb app, and nothing on screen depends on the answer.
   */
  markOpen: (conversationId, readCursor) =>
    secureApi
      .post(`${BASE}/${conversationId}/open`, { readCursor }, { preserveBackendError: true })
      .then((r) => r.data),

  /**
   * Claim / release. `preserveBackendError` on all three: "someone else took it" and "you are not
   * the owner" are different situations that must read differently, and secureApi otherwise rewrites
   * 4xx bodies to one generic string.
   */
  claim: (conversationId) =>
    secureApi.post(`${BASE}/${conversationId}/claim`, {}, { preserveBackendError: true }).then((r) => r.data),

  release: (conversationId) =>
    secureApi.post(`${BASE}/${conversationId}/release`, {}, { preserveBackendError: true }).then((r) => r.data),

  /**
   * Supervisor-only (admin / ops-manager); the server enforces it, this is the affordance.
   *
   * `expectedOwnerUserId` is a compare-and-set on the owner the supervisor was actually looking at.
   * The list refreshes on a timer, so the header can be a tick stale — without it, freeing "Sara's"
   * thread could silently yank a claim a colleague made seconds ago.
   */
  forceRelease: (conversationId, expectedOwnerUserId) =>
    secureApi
      .post(`${BASE}/${conversationId}/force-release`, { expectedOwnerUserId }, { preserveBackendError: true })
      .then((r) => r.data),

  // ---- Inc 7: context panel ----

  /**
   * Arrival evidence + door code for the booking behind this thread.
   *
   * ⚠️ NEVER awaited alongside the thread fetch, and nothing on screen may depend on it. The panel
   * loads BESIDE the messages and a failure here must not delay or break them — that is an
   * acceptance test, not a preference.
   *
   * `context: null` is a SUCCESS, not an absence of an answer: it means the thread has no booking
   * (an enquiry — roughly half of prod conversations). The caller renders nothing, never an error.
   *
   * `preserveBackendError` so «لوحة معلومات الحجز غير مفعّلة حالياً» (flag off, 403) stays
   * distinguishable from a genuine failure — secureApi otherwise rewrites 4xx bodies to one generic
   * string, and the two need different handling: one is expected, the other is worth logging.
   */
  context: (conversationId) =>
    secureApi
      .get(`${BASE}/${conversationId}/context`, { preserveBackendError: true })
      .then((r) => r.data),

  /**
   * What the team did with the «رسالة الاطمئنان» strip — 'inserted' | 'dismissed'
   * (tasks/stay-check-message.md D14). Recorded so the strip stays gone after a reload.
   */
  stayCheckPrompt: (conversationId, action) =>
    secureApi
      .post(`${BASE}/${conversationId}/stay-check-prompt`, { action })
      .then((r) => r.data),

  // ---- The shadow evaluator's unsent draft (Stage 3.5 Inc 4) ----

  /**
   * What the overnight automation WOULD have replied to this enquiry, and what actually happened.
   *
   * ⛔ NOTHING HERE SENDS. The draft is displayed greyed and labelled «الرد المقترح — لم يُرسل»; there
   * is no path from this screen to the guest, and there will not be one before Increment 6.
   *
   * `draft: null` is a SUCCESS and the ordinary case — most threads are not enquiries, and an
   * enquiry that arrived before the evaluator was switched on has no case. Render nothing.
   *
   * `preserveBackendError` for the same reason as `context` above: «الرد المقترح غير مفعّل حالياً»
   * (flag off, 403) must stay distinguishable from a genuine failure.
   */
  enquiryDraft: (conversationId) =>
    secureApi
      .get(`${BASE}/${conversationId}/enquiry-draft`, { preserveBackendError: true })
      .then((r) => r.data),

  /**
   * The operator's own reading of the guest's message — the ground truth the whole shadow period
   * exists to collect (D160). Either field may be sent alone; omitting one never blanks it.
   *
   * `preserveBackendError` is CONSUMED here, not decorative: the caller distinguishes
   * `ENQUIRY_SHADOW_STALE` (the guest revised — reload) from a generic failure, and shows different
   * Arabic for each.
   */
  /*
   * ⚠️ EVERY ANSWERABLE FIELD MUST BE LISTED HERE — a destructured allow-list silently DROPS
   * anything it does not name, and that is not a hypothetical: `safeToAutomate` and `unsafeReason`
   * shipped without being added, so the field the model comments call "the go-live question" was
   * posted by the page, discarded here, optimistically lit on screen, and gone on reload. The
   * operator sees a saved answer, the server never hears it, and the one dataset the send decision
   * rests on stays empty for the whole shadow period. Nothing fails; nothing logs.
   */
  labelEnquiryDraft: (conversationId, { intentLabel, humanContradiction, safeToAutomate, unsafeReason, enquiryVersion }) =>
    secureApi
      .post(`${BASE}/${conversationId}/enquiry-draft/label`,
        {
          // ⚠️ THE VERSION THE OPERATOR READ, always sent. The server scopes the write to it and
          // answers 409 if the guest has since revised — without it the answer lands on whichever
          // case is newest, which is a question the operator never saw.
          enquiryVersion,
          ...(intentLabel !== undefined ? { intentLabel } : {}),
          ...(humanContradiction !== undefined ? { humanContradiction } : {}),
          ...(safeToAutomate !== undefined ? { safeToAutomate } : {}),
          ...(unsafeReason !== undefined ? { unsafeReason } : {})
        },
        { preserveBackendError: true })
      .then((r) => r.data),

  // ---- Attachments: guest (and colleague) photos ----

  /**
   * One image from one message, as a Blob.
   *
   * ⚠️ THIS CANNOT BE AN `<img src>`, and that is not a style choice. Auth on every other call in
   * this file rides in the `x-auth-token` HEADER that secureApi's interceptor attaches; an `<img>`
   * tag issues its own request and sends no custom headers, so a src pointing at this endpoint would
   * be an unauthenticated request and 401 every time. Putting the token in the URL instead would put
   * a credential in browser history and server logs — which is the same mistake this whole endpoint
   * exists to avoid.
   *
   * So: fetch the bytes here, and let the caller wrap them in an object URL.
   *
   * ⚠️ THE CALLER OWNS THE LIFECYCLE. Every object URL created from this Blob must be revoked when
   * the component unmounts or the thread changes — an inbox tab stays open all shift, and unrevoked
   * blobs are a leak that grows with every photo scrolled past.
   *
   * `preserveBackendError` so «عرض المرفقات غير مفعّل حالياً» (flag off, 403) stays distinguishable
   * from a genuine upstream failure (502) — one is expected, the other is worth noticing.
   */
  attachment: (conversationId, messageId, index) =>
    secureApi
      .get(`${BASE}/${conversationId}/messages/${messageId}/attachment/${index}`, {
        responseType: 'blob',
        preserveBackendError: true
      })
      .then((r) => r.data),

  /**
   * A WhatsApp voice note or PDF, through the same route. It gets a longer wait than a photo: it can be up to 15 MB on
   * a phone connection, and the first view may still have to copy it from 4jawaly.
   */
  whatsappFile: (conversationId, messageId) =>
    secureApi
      .get(`${BASE}/${conversationId}/messages/${messageId}/attachment/0`, {
        responseType: 'blob',
        timeout: 60000,
        preserveBackendError: true
      })
      .then((r) => r.data),

  /**
   * One photo a COLLEAGUE sent, by send-record id (Inc 8, D133).
   *
   * Fetched rather than `src`-ed for the same reason as `attachment` above, plus one of its own: the
   * object sits under a private storage prefix, so its URL 403s in a browser even if we handed it
   * over. That is deliberate — these are guest-conversation photos, and a public URL is viewable by
   * anyone who obtains it, forever. Same caller-owns-the-object-URL rule applies.
   */
  replyPhotoImage: (conversationId, sendId) =>
    secureApi
      .get(`${BASE}/${conversationId}/reply-photo/${sendId}`, {
        responseType: 'blob',
        preserveBackendError: true
      })
      .then((r) => r.data)
};

export default conversationApi;
